import React, { useEffect, useState } from "react";
import { Breadcrumbs } from "../AdminLayout";
import { redirectsApi } from "./api";
import { formatErr } from "../api";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, ArrowRight } from "lucide-react";

export default function RedirectsManager() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ source_path: "", target_path: "", status_code: 301, active: true, notes: "" });

  const reload = () => redirectsApi.list().then(setRows).catch(e => toast.error(formatErr(e.response?.data?.detail)));
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const add = async () => {
    if (!form.source_path || !form.target_path) { toast.error("Both source and target are required"); return; }
    setBusy(true);
    try {
      await redirectsApi.create({ ...form, status_code: Number(form.status_code || 301) });
      setForm({ source_path: "", target_path: "", status_code: 301, active: true, notes: "" });
      toast.success("Redirect added"); reload();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggle = async (row) => { try { await redirectsApi.update(row.id, { active: !row.active }); reload(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };
  const remove = async (id) => {
    if (!window.confirm("Delete this redirect?")) return;
    try { await redirectsApi.remove(id); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const inputCls = "border border-line px-3 py-2 text-sm w-full";
  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="redirects-manager">
      <Toaster position="top-right" richColors/>
      <Breadcrumbs items={[{ label: "Site" }, { label: "Redirects" }]}/>
      <div className="font-serif text-3xl text-navy mt-4 mb-6">Redirect Manager</div>

      <div className="bg-white border border-line p-5 mb-5">
        <div className="mono text-[10px] uppercase tracking-widest text-prestige mb-3">Add new redirect</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <input data-testid="rd-source" placeholder="/old-path" value={form.source_path} onChange={e => setForm(f => ({ ...f, source_path: e.target.value }))} className={inputCls}/>
          <input data-testid="rd-target" placeholder="/new-path" value={form.target_path} onChange={e => setForm(f => ({ ...f, target_path: e.target.value }))} className={inputCls}/>
          <select data-testid="rd-code" value={form.status_code} onChange={e => setForm(f => ({ ...f, status_code: e.target.value }))} className={inputCls}>
            <option value={301}>301 Moved permanently</option>
            <option value={302}>302 Found (temp)</option>
            <option value={307}>307 Temporary</option>
            <option value={308}>308 Permanent</option>
          </select>
          <input data-testid="rd-notes" placeholder="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className={inputCls}/>
          <button data-testid="rd-add" onClick={add} disabled={busy} className="bg-navy hover:bg-navy-hover text-white text-sm px-4 py-2 inline-flex items-center gap-2 justify-center disabled:opacity-60"><Plus size={13}/> Add</button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white border border-line p-8 text-center text-sm text-stone2">No redirects yet.</div>
      ) : (
        <table className="w-full bg-white border border-line text-sm">
          <thead className="bg-clinical border-b border-line">
            <tr>
              <th className="text-left p-3 mono text-[10px] uppercase tracking-widest text-prestige">Source</th>
              <th className="text-left p-3 mono text-[10px] uppercase tracking-widest text-prestige">Target</th>
              <th className="text-left p-3 mono text-[10px] uppercase tracking-widest text-prestige">Code</th>
              <th className="text-left p-3 mono text-[10px] uppercase tracking-widest text-prestige">Hits</th>
              <th className="text-left p-3 mono text-[10px] uppercase tracking-widest text-prestige">Active</th>
              <th className="text-left p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`rd-row-${r.id}`} className="border-b border-line">
                <td className="p-3 mono text-xs text-navy">{r.source_path}</td>
                <td className="p-3 mono text-xs text-navy flex items-center gap-1"><ArrowRight size={11} className="text-stone2"/> {r.target_path}</td>
                <td className="p-3 mono text-[10px] text-stone2">{r.status_code}</td>
                <td className="p-3 mono text-[10px] text-stone2">{r.hit_count || 0}</td>
                <td className="p-3">
                  <button onClick={() => toggle(r)} data-testid={`rd-toggle-${r.id}`} className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 ${r.active ? "bg-[#3F6250]/10 text-[#3F6250]" : "bg-stone2/10 text-stone2"}`}>{r.active ? "active" : "inactive"}</button>
                </td>
                <td className="p-3"><button data-testid={`rd-remove-${r.id}`} onClick={() => remove(r.id)} className="text-[#9F1239]"><Trash2 size={14}/></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
